import { SendEventOnClick, SendEventOnView } from "~/components/Analytics"
import { useId } from "react"
import { ImageWidget } from "~/types/widgets"
import type { SelectPromotionParams } from "@decocms/apps-commerce/types"
import Image from "~/components/ui/Image"
import { clsx } from "~/sdk/clx"
import { isSchedulingActive, type Scheduling } from "~/sdk/contentScheduling"
import { toAnalytics } from "~/sdk/ga4/transform/toAnalytics"

/**
 * @titleBy alt
 */
interface Banner {
    /**
     * @title Texto alternativo
     */
    alt: string

    /**
     * @title Imagem
     */
    src: ImageWidget

    /**
     * @title Link
     */
    link: string

    /**
     * @title Parâmetros do GA4
     */
    ga4?: SelectPromotionParams

    /**
     * @title Agendamento
     * @description Deixe este campo vazio para manter sempre visível
     */
    scheduling?: Scheduling
}

export interface Props {
    /**
     * @maxItems 5
     * @minItems 5
     */
    banners?: Banner[]
}

const BannerLinks = ({ banners = [] }: Props) => {
    if (
        !banners?.length ||
        banners.length < 5 ||
        banners.some(({ src }) => !src) ||
        !banners.every(({ scheduling }) => isSchedulingActive(scheduling))
    ) {
        return null
    }

    return (
        <div className={clsx('flex py-6 lg:py-12 w-full')}>
            <ul
                className={clsx(
                    'auto-rows-auto gap-4 grid grid-cols-1 max-w-[1730px] mx-auto w-full',
                    'min-[480px]:grid-cols-2',
                    'lg:grid-cols-3',
                )}
            >
                {banners.map(({ src, alt, link, ga4 }, index) => {
                    const id = useId()

                    const analyticsParams = {
                        promotion_id: ga4?.promotion_id ?? 'banner_links',
                        promotion_name: ga4?.promotion_name ?? alt ?? 'Banner Links',
                        creative_name: ga4?.creative_name,
                        creative_slot: ga4?.creative_slot,
                        view: {
                            id: ga4?.promotion_id ?? 'banner_links',
                            name: ga4?.promotion_name ?? alt ?? 'Banner Links',
                        },
                    }

                    const viewPromotionEvent = toAnalytics({
                        type: 'view_promotion',
                        data: analyticsParams,
                    })

                    const selectPromotionEvent = toAnalytics({
                        type: 'select_promotion',
                        data: analyticsParams,
                    })

                    return (
                        <li
                            id={id}
                            className={clsx(
                                'flex w-full',
                                index === 1 && 'order-3',
                                index === 2 && 'min-[480px]:row-span-2 order-2',
                                index === 3 && 'order-4',
                                index === 4 && 'min-[480px]:order-5',
                            )}
                        >
                            <a
                                className={clsx(
                                    'duration-500 ease-in-out flex h-full max-h-[12.5rem] max-w-[21.5625rem] transition-[filter] w-full',
                                    'lg:max-h-[20.375rem] mx-auto lg:max-w-[35.125rem]',
                                    'hover:grayscale',
                                    index === 2 &&
                                        'max-h-[25.75rem] max-w-[21.5625rem] lg:max-h-[42.1875rem] lg:max-w-[35.25rem]',
                                )}
                                href={link}
                            >
                                <Image
                                    alt={alt}
                                    className='h-full object-cover w-full'
                                    height={index !== 2 ? 326.83 : 675}
                                    src={src}
                                    width={564}
                                    loading='lazy'
                                />
                            </a>

                            <SendEventOnView id={id} event={viewPromotionEvent} />
                            <SendEventOnClick id={id} event={selectPromotionEvent} />
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}

export const loader = () => {
}

export default BannerLinks
